
import { createSlice } from "@reduxjs/toolkit";
import apiService from "../../api/apiService";
import { endPoints } from "../../api/endpoints";
import { formatAxiosErrorMessage } from "../../utils/common";

const paymentListState = {
    list: {
        isLoading: false,
        data: null,
        error: null,
    },
    refund: {
        isLoading: false,
        data: null,
        error: null,
    },
    // Recording a manual (offline) payment
    create: {
        isLoading: false,
        data: null,
        error: null,
    },
    remove: {
        isLoading: false,
        data: null,
        error: null,
    },
};

const initialState = {
    ...paymentListState,
};

const paymentSlice = createSlice({
    name: "payment",
    initialState,
    reducers: {
        // Payment List Actions
        GetPaymentListPending: (state) => {
            state.list.isLoading = true;
            state.list.error = null;
        },
        GetPaymentListSuccess: (state, action) => {
            state.list.isLoading = false;
            state.list.data = action.payload;
            state.list.error = null;
        },
        GetPaymentListFailure: (state, action) => {
            state.list.isLoading = false;
            state.list.error = action.payload;
        },
        GetPaymentListReset: (state) => {
            state.list.isLoading = false;
            state.list.data = null;
            state.list.error = null;
        },

        // Refund Actions
        RefundPending: (state) => {
            state.refund.isLoading = true;
            state.refund.error = null;
            state.refund.data = null;
        },
        RefundSuccess: (state, action) => {
            state.refund.isLoading = false;
            state.refund.data = action.payload;
            state.refund.error = null;
        },
        RefundFailure: (state, action) => {
            state.refund.isLoading = false;
            state.refund.error = action.payload;
        },
        RefundReset: (state) => {
            state.refund.isLoading = false;
            state.refund.data = null;
            state.refund.error = null;
        },

        // Record Manual Payment Actions
        CreatePaymentPending: (state) => {
            state.create.isLoading = true;
            state.create.error = null;
            state.create.data = null;
        },
        CreatePaymentSuccess: (state, action) => {
            state.create.isLoading = false;
            state.create.data = action.payload;
            state.create.error = null;
        },
        CreatePaymentFailure: (state, action) => {
            state.create.isLoading = false;
            state.create.error = action.payload;
        },
        CreatePaymentReset: (state) => {
            state.create.isLoading = false;
            state.create.data = null;
            state.create.error = null;
        },

        DeletePaymentPending: (state) => {
            state.remove.isLoading = true;
            state.remove.error = null;
            state.remove.data = null;
        },
        DeletePaymentSuccess: (state, action) => {
            state.remove.isLoading = false;
            state.remove.data = action.payload;
            state.remove.error = null;
        },
        DeletePaymentFailure: (state, action) => {
            state.remove.isLoading = false;
            state.remove.error = action.payload;
        },
        DeletePaymentReset: (state) => {
            state.remove.isLoading = false;
            state.remove.data = null;
            state.remove.error = null;
        },
    },
});

export const {
    GetPaymentListPending,
    GetPaymentListSuccess,
    GetPaymentListFailure,
    GetPaymentListReset,
    RefundPending,
    RefundSuccess,
    RefundFailure,
    RefundReset,
    CreatePaymentPending,
    CreatePaymentSuccess,
    CreatePaymentFailure,
    CreatePaymentReset,
    DeletePaymentPending,
    DeletePaymentSuccess,
    DeletePaymentFailure,
    DeletePaymentReset,
} = paymentSlice.actions;

// API Actions
export const GetPaymentListApi = (params = "") => (dispatch) => {
    dispatch(GetPaymentListPending());
    return apiService
        .get(`${endPoints.payment.list}${params}`)
        .then((response) => {
            dispatch(GetPaymentListSuccess(response.data));
            return response.data;
        })
        .catch((error) => {
            dispatch(GetPaymentListFailure(formatAxiosErrorMessage(error)));
            throw error;
        });
};

export const RefundApi = (data) => (dispatch) => {
    dispatch(RefundPending());
    return apiService
        .post(endPoints.payment.refund, data)
        .then((response) => {
            dispatch(RefundSuccess(response.data));
            return response.data;
        })
        .catch((error) => {
            dispatch(RefundFailure(formatAxiosErrorMessage(error)));
            throw error;
        });
};

// Records a manual payment; the client balance updates asynchronously from the payment event
export const CreatePaymentApi = (data) => (dispatch) => {
    dispatch(CreatePaymentPending());
    return apiService
        .post(endPoints.payment.create, data)
        .then((response) => {
            dispatch(CreatePaymentSuccess(response.data));
            return response.data;
        })
        .catch((error) => {
            dispatch(CreatePaymentFailure(formatAxiosErrorMessage(error)));
            throw error;
        });
};


// Hard-delete a manual payment after voiding remaining balance
export const DeletePaymentApi = (paymentId) => (dispatch) => {
    dispatch(DeletePaymentPending());
    const url = endPoints.payment.delete.replace("{{id}}", paymentId);
    return apiService
        .delete(url)
        .then((response) => {
            dispatch(DeletePaymentSuccess(response.data));
            return response.data;
        })
        .catch((error) => {
            dispatch(DeletePaymentFailure(formatAxiosErrorMessage(error)));
            throw error;
        });
};

// Selector
export const PaymentState = (state) => state.payment;

export default paymentSlice.reducer;
